/**
 * Compact lease Overview banner for the tenant's checklist (BR-35). Shows only while relevant —
 * the window decision lives in `shouldShowChecklistBanner`; this component never re-derives it.
 */

import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { cn } from '@/lib/utils';
import { ChecklistStatusBadge } from './ChecklistStatusBadge';
import { phaseForLeaseState, shouldShowChecklistBanner, type BannerLease } from '../../lib/checklist-banner';
import type { ChecklistRow, WalkthroughPhase } from '../../lib/condition.api';
import type { UseChecklistsResult } from '../../lib/useChecklists';

interface TenantChecklistBannerProps {
  checklists: Pick<UseChecklistsResult, 'data' | 'status' | 'retry'>;
  lease: BannerLease;
  onView: (walkthroughId: string, phase: WalkthroughPhase) => void;
}

const slug = (phase: WalkthroughPhase) => (phase === 'MOVE_IN' ? 'move-in' : 'move-out');
const label = (phase: WalkthroughPhase) => `Tenant's ${slug(phase)} checklist`;

export function TenantChecklistBanner({ checklists, lease, onView }: TenantChecklistBannerProps) {
  const wrapper = 'mx-[var(--page-padding-x)] md:mx-4 mt-4 mb-4 flex flex-col gap-3';

  if (lease.state === 'archived') return null;

  const openPhase = phaseForLeaseState(lease.state);

  if (checklists.status === 'loading') {
    return openPhase ? (
      <div className={wrapper}>
        <Skeleton className="h-16 w-full" data-testid="lease-checklist-banner-loading" />
      </div>
    ) : null;
  }

  if (checklists.status === 'error') {
    if (!openPhase) return null;
    return (
      <div className={wrapper}>
        <div
          role="status"
          data-testid={`lease-checklist-banner-${slug(openPhase)}`}
          className="flex flex-col gap-2 rounded-xl border border-border bg-card p-[var(--card-padding)]"
        >
          <span className="text-sm font-medium text-foreground">{label(openPhase)}</span>
          <ChecklistStatusBadge state="ERROR" data-testid={`checklist-state-${slug(openPhase)}`} />
          <Button
            type="button"
            variant="ghost"
            className="self-start px-1 text-foreground"
            onClick={checklists.retry}
            data-testid="checklist-retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const now = new Date();
  const visible = (checklists.data ?? []).filter((row: ChecklistRow) =>
    shouldShowChecklistBanner({ phase: row.phase, tenant: row.tenant, lease, now }),
  );
  if (visible.length === 0) return null;

  return (
    <div className={wrapper}>
      {visible.map((row) => {
        const phaseSlug = slug(row.phase);
        const submitted = row.tenant.state === 'SUBMITTED';
        const inProgress = row.tenant.state === 'IN_PROGRESS';
        return (
          <div
            key={row.phase}
            role="status"
            data-testid={`lease-checklist-banner-${phaseSlug}`}
            className={cn(
              'flex flex-col gap-2 rounded-xl border p-[var(--card-padding)] md:flex-row md:items-center md:justify-between',
              submitted && 'border-success/25 bg-success/10',
              inProgress && 'border-primary/25 bg-primary/10',
              !submitted && !inProgress && 'border-border bg-card',
            )}
          >
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
              <span className="text-sm font-medium text-foreground">{label(row.phase)}</span>
              <ChecklistStatusBadge
                state={row.tenant.state}
                submittedAt={row.tenant.submittedAt}
                optional={row.tenant.optional}
                data-testid={`checklist-state-${phaseSlug}`}
              />
            </div>
            {submitted && row.tenant.walkthroughId && (
              <Button
                type="button"
                variant="outline"
                className="w-full text-foreground md:w-auto"
                onClick={() => onView(row.tenant.walkthroughId as string, row.phase)}
                data-testid={`checklist-view-${phaseSlug}`}
              >
                View checklist
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
